import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadReceipt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { shipmentMeta } from "@/lib/shipment";
import { toast } from "sonner";
import { Loader2, Package, CheckCircle2, DollarSign, Plus, MapPin, Route, FileText, Download } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-500">{label}</span>
        <span className={`grid place-items-center w-9 h-9 rounded-lg ${accent}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="mt-2 font-display font-extrabold text-2xl text-ksy-navy">{value}</div>
    </div>
  );
}

const badge = (s) =>
  s === "paid" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/my/bookings"), api.get("/my/quotes")])
      .then(([b, q]) => { setBookings(b.data); setQuotes(q.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const paid = bookings.filter((b) => b.payment_status === "paid");
  const spent = paid.reduce((s, b) => s + Number(b.total || 0), 0);

  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownload = async (id) => {
    setDownloadingId(id);
    try {
      await downloadReceipt(id);
    } catch {
      toast.error("Could not download receipt. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <DashboardShell title={`Welcome, ${user?.name || "there"}`} subtitle="Track your shipments and requests.">
      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-ksy-blue animate-spin" /></div>
      ) : (
        <div className="space-y-8" data-testid="client-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Package} label="Bookings" value={bookings.length} accent="bg-blue-50 text-ksy-blue" />
            <Stat icon={CheckCircle2} label="Paid" value={paid.length} accent="bg-green-50 text-green-600" />
            <Stat icon={DollarSign} label="Total spent" value={money(spent)} accent="bg-green-50 text-green-600" />
            <Stat icon={FileText} label="Quote requests" value={quotes.length} accent="bg-blue-50 text-ksy-blue" />
          </div>

          <div className="flex justify-between items-center">
            <h2 className="font-display font-bold text-xl text-ksy-navy">Your bookings</h2>
            <Link to="/#book" className="inline-flex items-center gap-2 bg-ksy-blue text-white font-bold px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors" data-testid="new-booking-btn">
              <Plus className="w-4 h-4" /> New booking
            </Link>
          </div>

          {bookings.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="no-bookings">
              No bookings yet. <Link to="/#book" className="font-bold text-ksy-blue">Book your first transport →</Link>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.id} className="p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`booking-${b.id}`}>
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-ksy-navy"><MapPin className="w-4 h-4 text-ksy-blue" />{b.origin} → {b.destination}</div>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                      <span className="flex items-center gap-1"><Route className="w-3.5 h-3.5" />{Number(b.miles).toLocaleString()} mi</span>
                      <span>{b.num_cars} vehicle(s)</span>
                      <span>{new Date(b.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-display font-extrabold text-lg text-ksy-navy">{money(b.total)}</span>
                    {(b.payment_status === "paid" || b.assigned_partner_email) && (
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${shipmentMeta(b.shipment_status).color}`} data-testid={`track-${b.id}`}>
                        {shipmentMeta(b.shipment_status).label}
                      </span>
                    )}
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${badge(b.payment_status)}`}>
                      {b.payment_status === "paid" ? "Paid" : "Pending"}
                    </span>
                    {b.payment_status === "paid" && (
                      <button
                        onClick={() => handleDownload(b.id)}
                        disabled={downloadingId === b.id}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-ksy-blue border border-ksy-blue/30 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                        data-testid={`download-receipt-${b.id}`}
                      >
                        {downloadingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Receipt
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {quotes.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-xl text-ksy-navy mb-4">Quote requests</h2>
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="quotes-list">
                {quotes.map((q) => (
                  <div key={q.id} className="p-5 flex items-center justify-between" data-testid={`quote-${q.id}`}>
                    <div className="font-semibold text-ksy-navy">{q.origin} → {q.destination}</div>
                    <span className="text-sm text-slate-500">{q.vehicle_year} {q.vehicle_make} {q.vehicle_model}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
